/**
 * トレーニング経過(D-197)の読み書き。書き込みは画面のボタン(管理者の操作)からだけ。
 *  - training_tracks/{op_id}_{business}: 稼働開始日と、採用データの日別の数字(7/31 まで)
 *  - training_records/{自動ID}: 面談・研修の記録(日付・種類・段階・担当・レポート)
 *  - training_milestones/{op_id}_{business}_{h}: 段階に届いたときの成績の控え
 * どれも admin だけが読み書きできる(firestore.rules)。
 */
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { writeAuditLog } from '@core/lib/auditLog';
import type { BusinessType, DateStr } from '@core/lib/types';
import { buildTracks, SHEET_UNTIL, savedMilestoneKey, trackKey, trainingAlerts, type MilestoneResult, type TrainingAlert, type SavedMilestone, type Track, type TrainingRecord, type TrainingTrackDoc } from './training';
import type { InterviewImportEntry, SheetImportEntry } from './trainingSheet';
import { fetchOperators } from '@core/masters/operatorRepo';
import { fetchSummariesRange } from '@core/dashboard/dashboardRepo';

const withId = <T>(d: { id: string; data: () => unknown }): T => ({ id: d.id, ...(d.data() as object) }) as T;

export async function fetchTrainingTracks(): Promise<TrainingTrackDoc[]> {
  const snap = await getDocs(collection(getDb(), 'training_tracks'));
  return snap.docs.map((d) => withId<TrainingTrackDoc>(d));
}

export async function fetchTrainingRecords(): Promise<TrainingRecord[]> {
  const snap = await getDocs(collection(getDb(), 'training_records'));
  return snap.docs.map((d) => withId<TrainingRecord>(d)).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

export async function fetchSavedMilestones(): Promise<SavedMilestone[]> {
  const snap = await getDocs(collection(getDb(), 'training_milestones'));
  return snap.docs.map((d) => withId<SavedMilestone>(d));
}

export type TrainingRecordInput = Pick<TrainingRecord, 'op_id' | 'business' | 'date' | 'kind' | 'milestone' | 'trainer' | 'report'> & Required<Pick<TrainingRecord, 'issues' | 'actions'>>;

export async function createTrainingRecord(input: TrainingRecordInput, userId: string): Promise<string> {
  const ref = await addDoc(collection(getDb(), 'training_records'), {
    ...input,
    created_by: userId,
    created_at: serverTimestamp(),
    updated_by: userId,
    updated_at: serverTimestamp(),
  });
  await writeAuditLog({ targetType: 'training', targetId: input.op_id, action: 'training_record_create', by: userId, after: { record_id: ref.id, date: input.date, kind: input.kind, milestone: input.milestone } });
  return ref.id;
}

export async function updateTrainingRecord(id: string, input: TrainingRecordInput, userId: string): Promise<void> {
  await updateDoc(doc(getDb(), 'training_records', id), { ...input, updated_by: userId, updated_at: serverTimestamp() });
  await writeAuditLog({ targetType: 'training', targetId: input.op_id, action: 'training_record_update', by: userId, after: { record_id: id, date: input.date, kind: input.kind, milestone: input.milestone } });
}

export async function deleteTrainingRecord(record: TrainingRecord, userId: string): Promise<void> {
  await deleteDoc(doc(getDb(), 'training_records', record.id));
  // 監査ログは閲覧者(viewer)も読めるので、レポートの本文・担当は残さない(何を消したかだけ)
  await writeAuditLog({
    targetType: 'training',
    targetId: record.op_id,
    action: 'training_record_delete',
    by: userId,
    before: { record_id: record.id, business: record.business, date: record.date, kind: record.kind, milestone: record.milestone },
  });
}

/** 稼働開始日を管理者が指定する(null で指定を外す。採用データの値があれば採用データに戻す) */
export async function setTrackStartDate(opId: string, business: BusinessType, startDate: DateStr | null, sheetStart: DateStr | null, userId: string): Promise<void> {
  const ref = doc(getDb(), 'training_tracks', trackKey(opId, business));
  const before = (await getDoc(ref)).data() ?? null;
  const next = startDate ? { start_date: startDate, start_source: 'manual' } : sheetStart ? { start_date: sheetStart, start_source: 'sheet' } : { start_date: null, start_source: null };
  await setDoc(ref, { op_id: opId, business, ...next, updated_by: userId, updated_at: serverTimestamp() }, { merge: true });
  await writeAuditLog({ targetType: 'training', targetId: opId, action: 'training_start_date', by: userId, before: before ? { business, start_date: before.start_date ?? null, start_source: before.start_source ?? null } : null, after: { business, ...next } });
}

/** 面談予定日を入れる・消す(null)。2026-10-15 発注者指示 */
export async function setInterviewPlanDate(opId: string, business: BusinessType, date: DateStr | null, userId: string): Promise<void> {
  const ref = doc(getDb(), 'training_tracks', trackKey(opId, business));
  const before = (await getDoc(ref)).data() ?? null;
  await setDoc(ref, { op_id: opId, business, interview_plan_date: date, updated_by: userId, updated_at: serverTimestamp() }, { merge: true });
  await writeAuditLog({ targetType: 'training', targetId: opId, action: 'training_interview_plan', by: userId, before: { business, interview_plan_date: before?.interview_plan_date ?? null }, after: { business, interview_plan_date: date } });
}

/**
 * 面談・研修の記録の担当者名をまとめて置き換える(2026-10-09 発注者指示。取込で入った名前を直すため)。
 * 対象は呼び出し側で選んだ記録(担当者名が from と同じもの)。監査ログには件数だけ残す(名前は残さない)
 */
export async function renameTrainer(records: TrainingRecord[], from: string, to: string, userId: string): Promise<number> {
  const db = getDb();
  const targets = records.filter((r) => r.trainer === from);
  for (let i = 0; i < targets.length; i += 400) {
    const batch = writeBatch(db);
    for (const r of targets.slice(i, i + 400)) batch.update(doc(db, 'training_records', r.id), { trainer: to, updated_by: userId, updated_at: serverTimestamp() });
    await batch.commit();
  }
  await writeAuditLog({ targetType: 'training', targetId: 'trainer_rename', action: 'training_record_trainer_rename', by: userId, after: { count: targets.length } });
  return targets.length;
}

/**
 * 面談結果のシートの面談を、面談の記録としてまとめて登録する(プレビューで「取り込む」になったものだけ。2026-10-09)。
 * 段階(15h/30h…)はシートに無いので「段階と関係ない」。担当者はシートに無いので画面で入れた名前
 */
export async function importInterviewRecords(entries: InterviewImportEntry[], trainer: string, fileName: string, userId: string): Promise<number> {
  const db = getDb();
  const items = entries.filter((e) => e.status === 'add' && e.opId && e.date);
  for (let i = 0; i < items.length; i += 400) {
    const batch = writeBatch(db);
    for (const e of items.slice(i, i + 400)) {
      batch.set(doc(collection(db, 'training_records')), {
        op_id: e.opId,
        business: e.business,
        date: e.date,
        kind: 'interview',
        milestone: null,
        trainer,
        report: e.report,
        source_file: fileName,
        created_by: userId,
        created_at: serverTimestamp(),
        updated_by: userId,
        updated_at: serverTimestamp(),
      });
    }
    await batch.commit();
  }
  // 監査ログは閲覧者も読めるので、レポートの本文は残さない(件数とファイル名だけ)
  await writeAuditLog({ targetType: 'training', targetId: 'interview_import', action: 'training_record_import', by: userId, after: { file: fileName, count: items.length } });
  return items.length;
}

/**
 * 稼働開始日をまとめて変更する(貼り付けのプレビューで「変える」になったものだけ。2026-10-09)。
 * 1件ずつの変更と同じく「管理者の指定」(manual)として保存し、監査ログにまとめて残す。
 */
export async function setTrackStartDates(changes: { opId: string; business: BusinessType; date: DateStr; before: DateStr | null }[], userId: string): Promise<number> {
  const db = getDb();
  for (let i = 0; i < changes.length; i += 400) {
    const batch = writeBatch(db);
    for (const c of changes.slice(i, i + 400)) {
      batch.set(
        doc(db, 'training_tracks', trackKey(c.opId, c.business)),
        { op_id: c.opId, business: c.business, start_date: c.date, start_source: 'manual', updated_by: userId, updated_at: serverTimestamp() },
        { merge: true },
      );
    }
    await batch.commit();
  }
  await writeAuditLog({
    targetType: 'training',
    targetId: 'start_date_bulk',
    action: 'training_start_date',
    by: userId,
    after: { count: changes.length, items: changes.map((c) => ({ op_id: c.opId, business: c.business, before: c.before, after: c.date })) },
  });
  return changes.length;
}

/**
 * 採用データを取り込む(プレビューで確認したものだけ)。
 * 管理者が稼働開始日を指定済みの人は、その日付を残して日別の数字だけ入れ替える。
 */
export async function importSheetEntries(entries: SheetImportEntry[], existing: TrainingTrackDoc[], fileName: string, userId: string): Promise<number> {
  const db = getDb();
  const byId = new Map(existing.map((d) => [d.id, d]));
  // 1回の書き込みは500件まで
  for (let i = 0; i < entries.length; i += 400) {
    const batch = writeBatch(db);
    for (const e of entries.slice(i, i + 400)) {
      const cur = byId.get(e.key);
      const keepManual = cur?.start_source === 'manual';
      batch.set(
        doc(db, 'training_tracks', e.key),
        {
          op_id: e.opId,
          business: e.business,
          ...(keepManual ? {} : { start_date: e.startDate, start_source: e.startDate ? 'sheet' : null }),
          sheet_start_date: e.startDate,
          sheet_name: e.sheetName,
          sheet_days: e.days,
          sheet_file: fileName,
          imported_by: userId,
          imported_at: serverTimestamp(),
        },
        { merge: true },
      );
    }
    await batch.commit();
  }
  await writeAuditLog({ targetType: 'training', targetId: 'sheet_import', action: 'training_sheet_import', by: userId, after: { file: fileName, tracks: entries.length } });
  return entries.length;
}

/** 段階に届いたときの成績を控えとして保存する(保存していないもの・数字が変わったものだけ) */
export async function saveMilestones(items: { track: Track; milestone: MilestoneResult }[], userId: string): Promise<number> {
  const db = getDb();
  for (let i = 0; i < items.length; i += 400) {
    const batch = writeBatch(db);
    for (const { track, milestone: m } of items.slice(i, i + 400)) {
      batch.set(doc(db, 'training_milestones', savedMilestoneKey(track.opId, track.business, m.milestone)), {
        op_id: track.opId,
        business: track.business,
        milestone: m.milestone,
        reached_date: m.reachedDate,
        seg_appointments: m.segAppointments,
        seg_hours: m.segHours,
        cum_appointments: m.cumAppointments,
        cum_hours: m.cumHours,
        recorded_by: userId,
        recorded_at: serverTimestamp(),
      });
    }
    await batch.commit();
  }
  await writeAuditLog({ targetType: 'training', targetId: 'milestones', action: 'training_milestones_save', by: userId, after: { count: items.length } });
  return items.length;
}

/** ダッシュボードの「要対応」用: 面談の時期の人(読めないときは空) */
export async function fetchTrainingAlerts(today: DateStr): Promise<TrainingAlert[]> {
  const [operators, trackDocs, records, summaries] = await Promise.all([fetchOperators(), fetchTrainingTracks(), fetchTrainingRecords(), fetchSummariesRange(SHEET_UNTIL, today)]);
  return trainingAlerts(buildTracks({ operators, summaries, trackDocs, today }), records);
}
